/**
 * Floating button sizes, chosen per widget in the dashboard
 * (`branding.launcher_size`).
 *
 * Every class is written out in full on purpose: Tailwind's JIT only emits
 * utilities it can find as literal strings in src/**, so building them by
 * concatenation (`w-[${px}px]`) would ship a button with no size.
 *
 * Mobile grows less than desktop: a 72px button on a 375px screen covers
 * the host page's content.
 */
export type LauncherSize = 'default' | 'large' | 'xlarge';

interface LauncherSizeClasses {
  /** Button box: mobile / desktop (`xs:` = 480px and up). */
  button: string;
  icon: string;
  /**
   * Desktop panel height cap. The panel sits on top of the button inside the
   * host, which is 24px from the bottom edge, so the cap is
   * `100dvh - (24 bottom + button + 12 gap + 4 air)`. Without it a bigger
   * button pushes the panel header past the top of a short laptop screen.
   */
  panelMaxHeight: string;
}

export const LAUNCHER_SIZES: Record<LauncherSize, LauncherSizeClasses> = {
  // 48 / 56 px — the size every widget had before this option existed.
  default: {
    button: 'w-12 h-12 xs:w-14 xs:h-14',
    icon: 'w-5 h-5 xs:w-6 xs:h-6',
    panelMaxHeight: 'xs:max-h-[calc(100dvh-96px)]',
  },
  // 52 / 64 px
  large: {
    button: 'w-[52px] h-[52px] xs:w-16 xs:h-16',
    icon: 'w-[22px] h-[22px] xs:w-7 xs:h-7',
    panelMaxHeight: 'xs:max-h-[calc(100dvh-104px)]',
  },
  // 56 / 72 px
  xlarge: {
    button: 'w-14 h-14 xs:w-[72px] xs:h-[72px]',
    icon: 'w-6 h-6 xs:w-8 xs:h-8',
    panelMaxHeight: 'xs:max-h-[calc(100dvh-112px)]',
  },
};

/**
 * Absent or unknown values fall back to `default`, so a backend that ships a
 * new size before the widget does still renders a sane button.
 */
export function resolveLauncherSize(value: unknown): LauncherSize {
  return value === 'large' || value === 'xlarge' ? value : 'default';
}
